import {
  AbilityBuilder,
  MongoAbility,
  createMongoAbility,
} from '@casl/ability';
import { ViewContext } from 'src/store/user-store';
import { siteContextPermissions } from './site-context-permissions';

type CRUD = 'create' | 'read' | 'update' | 'delete';
type SUBJECTS =
  | 'OrganizationDetails'
  | 'TenantDomains'
  | 'TenantEmployeeJoinMethod'
  | 'TenantPassword'
  | 'TenantContactForm'
  | 'TenantEvents'
  | 'SiteReceptionDetails'
  | 'SitePolls'
  | 'TenantPolls'
  | 'SiteIndicators'
  | 'SiteIndicatorTypes'
  | 'SiteIndicatorsComparison'
  | 'Site'
  | 'TenantPermissions'
  | 'SiteAssets'
  | 'SiteGdpr'
  | 'Password'
  | 'TenantSiteDetails'
  | 'TenantReceptionDetails'
  | 'TenantAuditLogs'
  | 'SiteAuditLogs'
  | 'SitePropertyOwners'
  | 'SitePropertyMembers'
  | 'SitePropertyResidents'
  | 'SiteMobileAppDisplayHomeScreen'
  | 'SiteMobileAppDisplayAmenities'
  | 'SiteMobileAppDisplayNewsfeed'
  | 'SiteMobileAppDisplayPlaces'
  | 'SiteMobileAppDisplayServices'
  | 'SiteContactForm';

type Abilities = [CRUD, SUBJECTS];

interface TenantAdministrator {
  userToken: string;
}

type TenantEmployeesActions = CRUD | 'upload' | 'resendInvitation';

type TenantEmployeesAbilities = [TenantEmployeesActions, 'TenantEmployees'];

type TenantEmployeesAccessActions = CRUD | 'grant' | 'revoke';

type TenantEmployeesAccessAbilities = [
  TenantEmployeesAccessActions,
  'TenantEmployeesAccess',
];

type TenantEmployeesDisplayedApplicationsActions = CRUD | 'stopDisplaying';

type TenantEmployeesDisplayedApplicationsAbilities = [
  TenantEmployeesDisplayedApplicationsActions,
  'TenantEmployeesDisplayedApplications',
];

type TenantEmployeesJoinRequestActions = CRUD | 'approve' | 'reject';

type TenantEmployeesJoinRequestAbilities = [
  TenantEmployeesJoinRequestActions,
  'TenantEmployeesJoinRequest',
];

type TenantAdministratorsActions =
  | CRUD
  | 'updatePhoneNumber'
  | 'updateRole'
  | 'resendInvitation';

type TenantAdministratorsAbilities = [
  TenantAdministratorsActions,
  'TenantAdministrators' | TenantAdministrator,
];

type TenantApplicationsActions = CRUD | 'setAsDefault' | 'removeFromDefault';

type TenantApplicationsAbilities = [
  TenantApplicationsActions,
  'TenantApplications' | TenantAdministrator,
];

type SiteTenantsActions = CRUD | 'resendInvitation';

type SiteTenantsAbilities = [SiteTenantsActions, 'SiteTenants'];

type SiteAdministratorsActions =
  | CRUD
  | 'resendInvitation'
  | 'updatePhoneNumber'
  | 'updateRole';

type SiteAdministratorsAbilities = [
  SiteAdministratorsActions,
  'SiteAdministrators',
];

type SiteEventsActions = CRUD;

type SiteEventsAbilities = [SiteEventsActions, 'SiteEvents'];

type TenantPostsActions = CRUD | 'publish' | 'unpublish';

type TenantPostsAbilities = [TenantPostsActions, 'TenantPosts'];

type SitePostsActions = CRUD | 'publish' | 'unpublish';

type SitePostsAbilities = [SitePostsActions, 'SitePosts'];

type SiteApplicationsActions =
  | CRUD
  | 'setAsDefault'
  | 'removeFromDefault'
  | 'grantPermission'
  | 'revokePermission';

type SiteApplicationsAbilities = [SiteApplicationsActions, 'SiteApplications'];

type SitePermissionsActions = CRUD | 'grant' | 'revoke';

type SitePermissionsAbilities = [SitePermissionsActions, 'SitePermissions'];

type SiteFaqsActions = CRUD | 'publish' | 'unpublish';

type SiteFaqsAbilities = [SiteFaqsActions, 'SiteFaqs'];
type MessagesActions = CRUD | 'publish' | 'duplicate';

type SiteMessagesAbilities = [MessagesActions, 'SiteMessages'];

type TenantMessagesAbilities = [MessagesActions, 'TenantMessages'];

type SiteIssuesActions = 'read' | 'downloadXls' | 'editStatus';

type SiteIssuesAbilities = [SiteIssuesActions, 'SiteIssues'];

type IssueManagersActions = CRUD | 'resendInvitation';

type IssueManagersAbilities = [IssueManagersActions, 'IssueManagers'];

type IssuesManagementActions = 'read' | 'downloadXls' | 'editStatus';

type IssuesManagementAbilities = [IssuesManagementActions, 'IssuesManagement'];

//hilight
type SitePropertyMembersActions =
  | CRUD
  | 'resendInvitation'
  | 'block'
  | 'downloadXls';

type SitePropertyMembersAbilities = [
  SitePropertyMembersActions,
  'SitePropertyMembers',
];

type SitePropertyOwnersActions = CRUD | 'resendInvitation';

type SitePropertyOwnersAbilities = [
  SitePropertyOwnersActions,
  'SitePropertyOwners',
];

type SitePropertyResidentsActions = 'read' | 'update' | 'delete';

type SitePropertyResidentsAbilities = [
  SitePropertyResidentsActions,
  'SitePropertyResidents',
];

type PushNotificationsActions = CRUD | 'publish' | 'duplicate';

type SitePushNotificationsAbilities = [
  PushNotificationsActions,
  'SitePushNotifications',
];

type GuestsActions = CRUD | 'downloadXls';

type SiteGuestsAbilities = [GuestsActions, 'SiteGuests'];

type PartnershipsActions = CRUD;

type SitePartnershipsAbilities = [PartnershipsActions, 'SitePartnerships'];

type SiteAmenitiesActions = CRUD | 'upload' | 'block' | 'unblock';

type SiteAmenitiesAbilities = [SiteAmenitiesActions, 'SiteAmenities'];

type SiteBookingRulesActions = CRUD;

type SiteBookingRulesAbilities = [SiteBookingRulesActions, 'SiteBookingRules'];

type SiteBookingsActions = CRUD | 'confirmPresence' | 'finish' | 'cancel';

type SiteBookingsAbilities = [SiteBookingsActions, 'SiteBookings'];

export type AppAbility = MongoAbility<
  | Abilities
  | TenantAdministratorsAbilities
  | TenantEmployeesAbilities
  | TenantEmployeesJoinRequestAbilities
  | TenantEmployeesAccessAbilities
  | TenantApplicationsAbilities
  | TenantEmployeesDisplayedApplicationsAbilities
  | SiteTenantsAbilities
  | SiteAdministratorsAbilities
  | SiteEventsAbilities
  | TenantPostsAbilities
  | SitePostsAbilities
  | SiteApplicationsAbilities
  | SitePermissionsAbilities
  | SiteFaqsAbilities
  | SiteMessagesAbilities
  | TenantMessagesAbilities
  | SiteIssuesAbilities
  | IssueManagersAbilities
  | IssuesManagementAbilities
  | SitePropertyOwnersAbilities
  | SitePropertyResidentsAbilities
  | SitePropertyMembersAbilities
  | SitePushNotificationsAbilities
  | SiteGuestsAbilities
  | SitePartnershipsAbilities
  | SiteAmenitiesAbilities
  | SiteBookingRulesAbilities
  | SiteBookingsAbilities
>;

export type Role =
  | 'owner'
  | 'admin'
  | 'readonly'
  | 'master'
  | 'buildingAdmin'
  | 'concierge';

export function definePermissions(
  viewContext: ViewContext,
  role: Role,
  userToken: string,
) {
  const builder = new AbilityBuilder<AppAbility>(createMongoAbility);

  switch (viewContext) {
    case 'site':
      siteContextPermissions(builder, role, userToken);
      break;
    default:
      break;
  }

  return builder.build();
}
